import { sumBy } from 'lodash';
import {
  HoldingClassRealisation,
  WithholdingTaxAllocation,
  InvestmentAllocation,
} from 'further-types/lp/realisation';
import getHoldingClassInvestmentsConsiderationValues from './get-holding-class-investments-consideration-values';
import calculateProportionalConsideration from './calculate-proportional-consideration';
import calculateTaxAllocation from './calculate-tax-allocation';
import getInvestorsWithholdingTax from './get-investors-withholding-tax';

const calculateNewInvestorAllocation = ({
  holdingClassId,
  investmentId,
  commitmentId,
  newAllocationPercentage,
  holdingClassRealisations,
  withholdingTaxList,
  investorAllocations,
}: {
  holdingClassId: string;
  investmentId: string;
  commitmentId: string;
  newAllocationPercentage: number;
  holdingClassRealisations: Array<HoldingClassRealisation>;
  withholdingTaxList: Array<WithholdingTaxAllocation>;
  investorAllocations: Array<InvestmentAllocation>;
}) => {
  const newAllocationPercentageDecimal = newAllocationPercentage / 100;
  const holdingClassInvestmentsWithConsiderationValues =
    getHoldingClassInvestmentsConsiderationValues({
      holdingClassRealisations,
      withholdingTaxList,
    });

  const holdingClassInvestment =
    holdingClassInvestmentsWithConsiderationValues.find(
      (holdingClassInvestment) =>
        holdingClassInvestment.holdingClassId === holdingClassId &&
        holdingClassInvestment.investmentId === investmentId,
    );

  const newUnitsAllocated =
    (holdingClassInvestment?.unitsRealised ?? 0) *
    newAllocationPercentageDecimal;

  const newConsiderationValue = calculateProportionalConsideration(
    holdingClassInvestment?.receivedConsiderationTotal ?? 0,
    newAllocationPercentageDecimal,
  );

  const newEscrowConsiderationValue = calculateProportionalConsideration(
    holdingClassInvestment?.escrowConsiderationTotal ?? 0,
    newAllocationPercentageDecimal,
  );

  const newEarnoutConsiderationValue = calculateProportionalConsideration(
    holdingClassInvestment?.earnoutConsiderationTotal ?? 0,
    newAllocationPercentageDecimal,
  );

  const investorsWithholdingTax =
    getInvestorsWithholdingTax(withholdingTaxList);

  const taxAllocated = investorAllocations.filter(
    (investmentAllocation) =>
      investmentAllocation.investmentId === investmentId &&
      investmentAllocation.holdingClassId === holdingClassId &&
      investorsWithholdingTax?.find(
        (withholdingTaxInvestor) =>
          withholdingTaxInvestor.commitmentId ===
          investmentAllocation.commitmentId,
      ),
  );

  const nonTaxExemptAllocationPercentage = sumBy(
    taxAllocated,
    'allocationPercentage',
  );

  const { effectiveTaxAllocationPercentage } = calculateTaxAllocation(
    withholdingTaxList,
    commitmentId,
    newAllocationPercentageDecimal,
    nonTaxExemptAllocationPercentage,
  );

  const newWithholdingTaxPayable = calculateProportionalConsideration(
    holdingClassInvestment?.withholdingTaxPayable ?? 0,
    effectiveTaxAllocationPercentage,
  );

  return {
    newUnitsAllocated,
    newConsiderationValue,
    newEscrowConsiderationValue,
    newEarnoutConsiderationValue,
    newWithholdingTaxPayable,
  };
};

export default calculateNewInvestorAllocation;
